<template>
    <tbody class="valuation-subject--recap-section">
        <tr class="section-title-row">
            <td
                :colspan="subject.session_list.length + 1"
            >
                {{$t("general:summary")}}
            </td>
        </tr>
        <tr>
            <td>
                {{$t("task_details:recap:estimated_value")}}
            </td>
            <td
                v-for="(session, session_idx) in subject.session_list" 
                :key="'vs-recap-appraisal-value-td-session-'+session_idx"
                class="text-center media-middle"
            >
                <div 
                    class="text-primary text-semibold" style="font-size: 16px;"
                >
                    <div v-if="isLoading(session.id)">
                        <i class="fa fa-spinner fa-spin"></i>
                    </div>
                    <div v-else-if="getAppraisalValue(session.id)">
                        <a :href="`/amas/analysis/${session.id}/appraisal/recap`">
                            {{getAppraisalValue(session.id).appraised_value||"/"|formatCurrency}}
                            <span
                                v-if="getAppraisalValue(session.id).appraised_value"
                                v-tooltip="getAppraisalAvmGeneratedExplanationTooltip(session.id)"
                            >
                                <i class="fa fa-cogs" v-if="isAppraisalAvmGenerated(session.id)"></i>
                                <i class="fa fa-user" v-else></i>
                            </span>
                        </a>
                    </div>
                </div>
            </td>
        </tr>
        <tr>
            <td>
                {{$t("task_details:recap:estimated_value_per_m2")}}
            </td>
            <td
                v-for="(session, session_idx) in subject.session_list" 
                :key="'vs-recap-appraisal-value-m2-td-session-'+session_idx"
                class="text-center media-middle"
            >
                <div 
                    class="text-info text-semibold" style="font-size: 16px;"
                >
                    <div v-if="isLoading(session.id)">
                        <i class="fa fa-spinner fa-spin"></i>
                    </div>
                    <div v-else-if="getAppraisalValue(session.id)">
                        {{getAppraisalValue(session.id).appraised_value_m2||"/"|formatCurrencym2}}
                    </div>
                </div>
            </td>
        </tr>
        <tr>
            <td>
                {{$t("task_details:recap:arvio_avm_confidence")}}
            </td>
            <td
                v-for="(session, session_idx) in subject.session_list" 
                :key="'vs-recap-appraisal-value-m2-td-session-'+session_idx"
                class="text-center media-middle"
            >
                <div 
                    class="text-info text-semibold"
                >
                    <div v-if="isLoading(session.id)">
                        <i class="fa fa-spinner fa-spin"></i>
                    </div>
                    <div v-else-if="getAppraisalValue(session.id) && isAppraisalAvmGenerated(session.id)">
                        <span
                            :class="'recap-confidence-score-circle circle circle-sm bg-' + getAppraisalValue(session.id).appraisal_comparable_with_confidence_score.transactions_confidence_score_css"
                            v-tooltip="getConfidenceScoreTooltip(session.id)"
                        >
                            {{ getAppraisalValue(session.id).appraisal_comparable_with_confidence_score.transactions_confidence_score_label }}
                        </span>
                    </div>
                    <div v-else-if="getAppraisalValue(session.id)">
                        <!-- Manual appraisal -->
                        <span
                            class="recap-confidence-score-circle circle circle-sm bg-grey"
                            v-tooltip="$t('task_details:recap:confidence_score_avm_only')"
                        >
                            /
                        </span>
                    </div>
                </div>
            </td>
        </tr>
    </tbody>
</template>

<script>
export default {
    model: {
        prop: 'subject',
        event: 'change' 
    },
    props: {
        subject: {
            type: Object,
            default: () => {}
        }
    },
    methods: {
        getTaskStatus: function(session_id) {
            const task_status = this.$store.getters.getSingleSessionTaskStatus(session_id);
            return task_status || null;
        },
        getAppraisalValue: function(session_id) {
            return this.$store.getters.getSessionAppraisalValue(session_id);
        },
        isLoading: function(session_id) {
            // check that all value related task statuses are finished
            const valueRelatedTaskStatuses = ["comparable_transactions_sale", "cost_method"]; // extend when needed
            const allTaskStatuses = this.getTaskStatus(session_id);
            if (!allTaskStatuses) {
                return true;
            }
            for (const status of valueRelatedTaskStatuses) {
                const currentTaskStatus = allTaskStatuses[status];
                if (currentTaskStatus && (currentTaskStatus === "loading" || currentTaskStatus.status_code === "STARTED")) {
                    return true;
                }
            }

            // check that the appraised_value has a value
            const appraised_value = this.getAppraisalValue(session_id);
            if (appraised_value && (appraised_value.appraised_value || appraised_value.appraised_value_m2)) {
                return false;
            }
            return true;
        },
        getConfidenceScoreTooltip: function(session_id) {
            const appraisal_value = this.getAppraisalValue(session_id);
            if (!appraisal_value || !appraisal_value.appraisal_comparable_with_confidence_score || !appraisal_value.appraisal_comparable_with_confidence_score.transactions_confidence_score_label) {
                return "";
            }
            const label = appraisal_value.appraisal_comparable_with_confidence_score.transactions_confidence_score_label;
            const _map = {
                "A": this.$t("general:high"),
                "B": this.$t("general:medium"),
                "C": this.$t("general:low"),
            }
            return `${_map[label]} (${label})`;
        },
        isAppraisalAvmGenerated: function(session_id) {
            const appraisal_value = this.getAppraisalValue(session_id);
            if (!appraisal_value) {
                return false;
            }
            // Unpack 2 variables from the tuple
            const [is_appraisal_avm_generated, explanation] = appraisal_value.is_appraisal_avm_generated;
            return is_appraisal_avm_generated;
        },
        isAppraisalAvmGeneratedExplanation: function(session_id) {
            const appraisal_value = this.getAppraisalValue(session_id);
            if (!appraisal_value) {
                return "";
            }
            // Unpack 2 variables from the tuple
            const [is_appraisal_avm_generated, explanation] = appraisal_value.is_appraisal_avm_generated;
            return explanation;
        },
        getAppraisalAvmGeneratedExplanationTooltip: function(session_id) {
            if (this.isAppraisalAvmGenerated(session_id)) {
                return `${this.$t('task_details:recap:avm_generated_appraisal')}`;
            }
            return `${this.$t('task_details:recap:manual_appraisal')}. ${this.isAppraisalAvmGeneratedExplanation(session_id)}`;
        }
    },
    mounted() {
        // retrieve session appraisal values at the start
        for (const session of this.subject.session_list) {
            this.$store.dispatch("retrieveSessionAppraisalValue", session.id)
        }
    }
}
</script>
